import { Box, Collapse, Divider, IconButton, Paper, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { HighlightedFields, PlaceDetails } from '@/map/domain/entities/map.types';
import { BORDER_COLORS, RGBA_COLORS } from '@/common/presentation/constants/colors';
import { LocationFormFields } from './LocationFormFields';
import { LocationSearchInput } from './LocationSearchInput';
import { useLocationFormPanel } from '../hooks/useLocationFormPanel';
import tid from '@/common/infrastructure/config/tid';

export type LocationFormPanelProps = {
  onPlaceSelect: (place: google.maps.places.Place) => void;
  setMarkerPosition: (position: google.maps.LatLngLiteral) => void;
  selectedPlaceDetails: PlaceDetails | null;
  setSelectedPlaceDetails: (details: PlaceDetails | null) => void;
  markerPosition: google.maps.LatLngLiteral | null;
  highlightedFields?: HighlightedFields;
  updateFormValues: (details: PlaceDetails) => Promise<void>;
  hideCoordinates?: boolean;
  hideCountry?: boolean;
  panToWithOffset?: (position: google.maps.LatLngLiteral) => void;
};

export const LocationFormPanel = ({
  onPlaceSelect,
  setMarkerPosition,
  markerPosition,
  highlightedFields,
  updateFormValues,
  hideCoordinates = false,
  hideCountry = false,
  panToWithOffset
}: LocationFormPanelProps) => {
  const {
    inputValue,
    suggestions,
    isLoading,
    isDetailsOpen,
    setIsDetailsOpen,
    handleInputChange,
    handlePlaceSelect,
    handleCoordinatesChange,
    formik
  } = useLocationFormPanel({
    onPlaceSelect,
    setMarkerPosition,
    markerPosition,
    updateFormValues,
    hideCoordinates,
    hideCountry,
    panToWithOffset
  });

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'absolute',
        top: 8,
        left: 8,
        right: 8,
        zIndex: 20,
        borderRadius: 2,
        boxShadow: 3,
        background: RGBA_COLORS.WHITE_95,
        p: 2,
        maxWidth: '380px',
        width: '100%'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <LocationSearchInput
          inputValue={inputValue}
          suggestions={suggestions}
          isLoading={isLoading}
          onInputChange={handleInputChange}
          onSuggestionSelect={handlePlaceSelect}
        />
        {(() => {
          const { address, city, state, zipCode, latitude, longitude } = formik.values;
          const hasAddressData = address || city || state || zipCode;
          const hasCoordinates = latitude != null && longitude != null;
          const hasExistingData = hasAddressData || hasCoordinates || markerPosition;
          const isDisabled = !hasExistingData;
          const tooltipText = isDisabled ? 'Search for a location or right click to place in the map' : '';

          return (
            <Tooltip title={tooltipText} arrow placement="top" describeChild>
              <span role="group">
                <IconButton
                  size="small"
                  onClick={() => setIsDetailsOpen((prev) => !prev)}
                  disabled={isDisabled}
                  aria-label={isDetailsOpen ? 'Collapse location details' : 'Expand location details'}
                  sx={{
                    border: `1px solid ${BORDER_COLORS.LIGHT}`,
                    borderRadius: 1,
                    ml: 1
                  }}
                  {...tid('map:toggle-details-button')}
                >
                  {isDetailsOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              </span>
            </Tooltip>
          );
        })()}
      </Box>
      <Collapse in={isDetailsOpen}>
        <Divider sx={{ my: 2 }} />
        <LocationFormFields
          highlightedFields={highlightedFields}
          handleCoordinatesChange={handleCoordinatesChange}
          hideCoordinates={hideCoordinates}
          hideCountry={hideCountry}
        />
      </Collapse>
    </Paper>
  );
};
